/**
 * Demonstration dataset for the Evaluation workspace.
 *
 * Modelled on a UK public-sector open procedure run under the
 * Procurement Act 2023 (in force 24 Feb 2025), which replaced the Public
 * Contracts Regulations 2015 and swapped MEAT ("most economically
 * advantageous tender") for MAT ("most advantageous tender").
 *
 * Everything downstream of `rawScores` is COMPUTED by utils/evaluationEngine.
 * Nothing in this file hardcodes a subtotal, a total or a rank — adjusting a
 * band in the UI genuinely moves the result.
 */

export type Band = 0 | 1 | 2 | 3 | 4;

export const tender = {
  reference: 'ARA-2026-ITS-014',
  title: 'Provision of Managed IT Services 2027–2030',
  buyer: 'Aldercroft Research Agency',
  buyerType: 'Non-departmental public body',
  procedure: 'Open procedure',
  regime: 'Procurement Act 2023',
  awardBasis: 'Most Advantageous Tender (MAT)',
  ftsNotice: 'FTS 2026/S 041-118204',
  estimatedValue: 3_050_000,
  currency: 'GBP',
  term: '36 months + 24 months optional extension',
  lots: 1,
  // The live cycle: submissions are in, evaluation is mid-flight today.
  timeline: [
    { label: 'Tender notice published', date: '2026-06-12', done: true },
    { label: 'Clarification deadline', date: '2026-07-17', done: true },
    { label: 'Submission deadline', date: '2026-08-14', done: true, note: '14:00 BST' },
    { label: 'Evaluation window', date: '2026-08-17 → 2026-08-28', done: false, active: true },
    { label: 'Moderation & consensus', date: '2026-08-28', done: false },
    { label: 'Contract award notice', date: '2026-09-02', done: false },
    { label: 'Standstill ends', date: '2026-09-11', done: false, note: '8 working days' },
    { label: 'Contract start', date: '2026-10-01', done: false },
  ],
  documents: [
    { name: 'ARA-2026-ITS-014_ITT.pdf', kind: 'ITT', pages: 18, size: '1.6 MB', status: 'parsed' },
    { name: 'Schedule-2_Specification.pdf', kind: 'Specification', pages: 41, size: '2.7 MB', status: 'parsed' },
    { name: 'Schedule-4_Evaluation-Model.xlsx', kind: 'Evaluation model', pages: 6, size: '188 KB', status: 'parsed' },
    { name: 'Schedule-7_Social-Value.pdf', kind: 'Social value', pages: 12, size: '640 KB', status: 'parsed' },
    { name: 'Pricing-Matrix_v3.xlsx', kind: 'Pricing', pages: 4, size: '96 KB', status: 'parsed' },
  ],
};

/** Standard UK public-sector 0–4 evaluation scale. */
export const scoringScale: { band: Band; label: string; definition: string }[] = [
  { band: 4, label: 'Excellent', definition: 'Comprehensively meets the requirement with substantial added value and compelling evidence throughout.' },
  { band: 3, label: 'Good', definition: 'Meets the requirement in full with good supporting evidence; minor gaps only.' },
  { band: 2, label: 'Acceptable', definition: 'Meets the requirement in most respects; evidence is adequate but limited in places.' },
  { band: 1, label: 'Poor', definition: 'Partially addresses the requirement; material gaps or unsupported assertions.' },
  { band: 0, label: 'Unacceptable', definition: 'Fails to address the requirement or the response is absent.' },
];

/**
 * Award criteria. Quality 60 / Price 40.
 * Social value carries 10 of the 100 points, meeting the PPN 002 minimum
 * 10% weighting mandatory for in-scope bodies from 1 October 2025.
 */
export const criteria = [
  {
    id: 'Q1', name: 'Technical Approach & Service Delivery', weight: 22, kind: 'quality' as const,
    source: { doc: 'ARA-2026-ITS-014_ITT.pdf', page: 9, clause: '§5.3' },
    confidence: 0.97,
    sub: [
      { id: 'Q1.1', name: 'Service model & transition', weight: 8, source: { doc: 'Schedule-2_Specification.pdf', page: 9, clause: '§2.1' }, confidence: 0.96 },
      { id: 'Q1.2', name: 'Toolchain, automation & AI adoption', weight: 7, source: { doc: 'Schedule-2_Specification.pdf', page: 14, clause: '§2.6' }, confidence: 0.94 },
      { id: 'Q1.3', name: 'SLA & performance management', weight: 7, source: { doc: 'Schedule-2_Specification.pdf', page: 19, clause: '§3.4' }, confidence: 0.98 },
    ],
  },
  {
    id: 'Q2', name: 'Security & Information Governance', weight: 16, kind: 'quality' as const,
    source: { doc: 'ARA-2026-ITS-014_ITT.pdf', page: 10, clause: '§5.4' },
    confidence: 0.98,
    sub: [
      { id: 'Q2.1', name: 'Cyber controls & certification', weight: 8, source: { doc: 'Schedule-2_Specification.pdf', page: 26, clause: '§4.1' }, confidence: 0.99 },
      { id: 'Q2.2', name: 'Incident response & resilience', weight: 8, source: { doc: 'Schedule-2_Specification.pdf', page: 30, clause: '§4.5' }, confidence: 0.95 },
    ],
  },
  {
    id: 'Q3', name: 'Experience & Capability', weight: 12, kind: 'quality' as const,
    source: { doc: 'ARA-2026-ITS-014_ITT.pdf', page: 10, clause: '§5.5' },
    confidence: 0.93,
    sub: [
      { id: 'Q3.1', name: 'Comparable contracts & references', weight: 7, source: { doc: 'ARA-2026-ITS-014_ITT.pdf', page: 10, clause: '§5.5.1' }, confidence: 0.92 },
      { id: 'Q3.2', name: 'Team, capacity & key personnel', weight: 5, source: { doc: 'ARA-2026-ITS-014_ITT.pdf', page: 10, clause: '§5.5.2' }, confidence: 0.90 },
    ],
  },
  {
    id: 'Q4', name: 'Social Value', weight: 10, kind: 'quality' as const,
    source: { doc: 'Schedule-7_Social-Value.pdf', page: 3, clause: '§1.2' },
    confidence: 0.91,
    statutory: 'PPN 002 — minimum 10% weighting',
    sub: [
      { id: 'Q4.1', name: 'Local skills, employment & SME spend', weight: 5, source: { doc: 'Schedule-7_Social-Value.pdf', page: 5, clause: '§2.1' }, confidence: 0.90 },
      { id: 'Q4.2', name: 'Environmental impact & net zero', weight: 5, source: { doc: 'Schedule-7_Social-Value.pdf', page: 8, clause: '§2.4' }, confidence: 0.92 },
    ],
  },
  {
    id: 'P1', name: 'Price', weight: 40, kind: 'price' as const,
    source: { doc: 'Pricing-Matrix_v3.xlsx', page: 1, clause: 'Tab 1' },
    confidence: 0.99,
    method: 'Lowest compliant price ÷ tendered price × 40',
    sub: [],
  },
];

export const bidders = [
  {
    id: 'calderwood', name: 'Calderwood Technologies Ltd', shortName: 'Calderwood',
    companyNumber: '09482113', smeStatus: 'Large', employees: 640, hq: 'Reading',
    price: 3_480_000,
    // Stored UTC; BST is UTC+1, so this renders as the 16:42 BST quoted in
    // the compliance evidence. Keep the pair in step if either is edited.
    submittedAt: '2026-08-13T15:42:00Z',
    documents: 6, pages: 214, sizeMb: 18.4,
    hash: 'a3f9…c17e',
  },
  {
    id: 'strathmere', name: 'Strathmere Solutions Ltd', shortName: 'Strathmere',
    companyNumber: '07711952', smeStatus: 'SME', employees: 185, hq: 'Manchester',
    price: 3_215_000,
    submittedAt: '2026-08-14T10:08:00Z',
    documents: 8, pages: 268, sizeMb: 22.1,
    hash: '7b21…9d04',
  },
  {
    id: 'havelock', name: 'Havelock Digital Group', shortName: 'Havelock',
    companyNumber: '11209864', smeStatus: 'SME', employees: 96, hq: 'Bristol',
    price: 3_690_000,
    submittedAt: '2026-08-14T12:51:00Z',
    documents: 5, pages: 176, sizeMb: 14.9,
    hash: 'e58c…2f6b',
  },
  {
    id: 'kelverton', name: 'Kelverton Managed Services Ltd', shortName: 'Kelverton',
    companyNumber: '08633127', smeStatus: 'SME', employees: 240, hq: 'Birmingham',
    price: 3_340_000,
    submittedAt: '2026-08-12T08:24:00Z',
    documents: 7, pages: 231, sizeMb: 19.7,
    hash: '4d7a…b3c8',
  },
  {
    id: 'marchford', name: 'Marchford Systems Ltd', shortName: 'Marchford',
    companyNumber: '10442078', smeStatus: 'SME', employees: 72, hq: 'Sheffield',
    price: 3_120_000,
    submittedAt: '2026-08-14T08:37:00Z',
    documents: 5, pages: 149, sizeMb: 11.6,
    hash: 'f210…6e9d',
  },
  {
    id: 'brayford', name: 'Brayford Technology Partners Ltd', shortName: 'Brayford',
    companyNumber: '12007751', smeStatus: 'SME', employees: 118, hq: 'Nottingham',
    price: 3_050_000,
    submittedAt: '2026-08-14T13:11:00Z',
    documents: 6, pages: 187, sizeMb: 15.3,
    hash: '9c53…a0f7',
  },
  {
    id: 'pellworth', name: 'Pellworth IT Services', shortName: 'Pellworth',
    companyNumber: '13884401', smeStatus: 'SME', employees: 54, hq: 'Leeds',
    price: 2_940_000,
    submittedAt: '2026-08-14T12:58:00Z',
    documents: 4, pages: 118, sizeMb: 9.2,
    hash: 'c904…81aa',
  },
];

/**
 * Compliance gate. Under the Act a tender that fails a mandatory requirement
 * or an exclusion ground is disqualified BEFORE quality assessment — it is
 * never scored and never ranked.
 */
export const complianceChecks = [
  { id: 'M1', label: 'Tender received before deadline', basis: 'ITT §4.1' },
  { id: 'M2', label: 'Pricing matrix complete, all lines priced', basis: 'ITT §6.2' },
  { id: 'M3', label: 'Cyber Essentials Plus certificate in date', basis: 'Spec §4.1' },
  { id: 'M4', label: 'ISO/IEC 27001 certification or equivalent evidence', basis: 'Spec §4.1' },
  { id: 'M5', label: 'Employer liability insurance ≥ £10m', basis: 'ITT §7.3' },
  { id: 'M6', label: 'All mandatory response sections addressed', basis: 'ITT §5.1' },
  { id: 'M7', label: 'No mandatory exclusion grounds declared', basis: 'PA 2023 Sch. 6' },
];

export const complianceResults: Record<string, { status: 'pass' | 'flag' | 'fail'; note?: string; evidence?: string }[]> = {
  calderwood: [
    { status: 'pass', evidence: 'Received 13 Aug 16:42 BST' },
    { status: 'pass', evidence: 'Pricing-Matrix rev. C, 41/41 lines' },
    { status: 'pass', evidence: 'CE+ cert IASME-CE-P-882411, exp. 2027-03-19' },
    { status: 'pass', evidence: 'ISO 27001:2022, cert 2411-QMS-0117' },
    { status: 'pass', evidence: '£10m EL, Aviva policy 88214/C' },
    { status: 'pass', evidence: '6/6 sections addressed' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
  strathmere: [
    { status: 'pass', evidence: 'Received 14 Aug 11:08 BST' },
    { status: 'pass', evidence: 'Pricing-Matrix rev. C, 41/41 lines' },
    { status: 'pass', evidence: 'CE+ cert IASME-CE-P-903117, exp. 2027-01-08' },
    { status: 'pass', evidence: 'ISO 27001:2022, cert 3318-ISMS-0442' },
    { status: 'flag', note: 'Certificate names parent entity "Strathmere Group Holdings Ltd"; clarification issued 18 Aug, response accepted 20 Aug.', evidence: '£10m EL, Zurich policy PZ-4471-09' },
    { status: 'pass', evidence: '6/6 sections addressed' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
  havelock: [
    { status: 'pass', evidence: 'Received 14 Aug 13:51 BST' },
    { status: 'pass', evidence: 'Pricing-Matrix rev. C, 41/41 lines' },
    { status: 'pass', evidence: 'CE+ cert IASME-CE-P-914806, exp. 2026-11-30' },
    { status: 'flag', note: 'ISO 27001 lapsed 2026-05-31; UKAS-accredited recertification audit evidence supplied and accepted as equivalent under Spec §4.1(b).', evidence: 'Recert audit report, 2026-07-22' },
    { status: 'pass', evidence: '£10m EL, AXA policy 20117745' },
    { status: 'pass', evidence: '6/6 sections addressed' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
  kelverton: [
    { status: 'pass', evidence: 'Received 12 Aug 09:24 BST' },
    { status: 'pass', evidence: 'Pricing-Matrix rev. C, 41/41 lines' },
    { status: 'pass', evidence: 'CE+ cert IASME-CE-P-897330, exp. 2027-06-04' },
    { status: 'pass', evidence: 'ISO 27001:2022, cert 5502-ISMS-0918' },
    { status: 'pass', evidence: '£10m EL, RSA policy RS-3318-22' },
    { status: 'pass', evidence: '6/6 sections addressed' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
  marchford: [
    { status: 'pass', evidence: 'Received 14 Aug 09:37 BST' },
    { status: 'flag', note: 'Rate-card tab submitted on a superseded template (rev. B). Clarification issued 18 Aug; rev. C resubmitted with identical figures and accepted.', evidence: 'Pricing-Matrix rev. B → C' },
    { status: 'pass', evidence: 'CE+ cert IASME-CE-P-926714, exp. 2027-02-11' },
    { status: 'pass', evidence: 'ISO 27001:2022, cert 6127-ISMS-0233' },
    { status: 'pass', evidence: '£10m EL, Allianz policy AZ-77104' },
    { status: 'pass', evidence: '6/6 sections addressed' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
  brayford: [
    { status: 'fail', note: 'Tender uploaded at 14:11:38 BST on 14 August, 11 minutes and 38 seconds after the 14:00 deadline. The portal timestamp is determinative under ITT §4.1 and the tender must be disregarded.', evidence: 'Portal receipt 14 Aug 14:11:38 BST' },
    { status: 'pass', evidence: 'Pricing-Matrix rev. C, 41/41 lines' },
    { status: 'pass', evidence: 'CE+ cert IASME-CE-P-880219, exp. 2027-04-27' },
    { status: 'pass', evidence: 'ISO 27001:2022, cert 4471-ISMS-0605' },
    { status: 'pass', evidence: '£10m EL, Aviva policy 91277/B' },
    { status: 'pass', evidence: '6/6 sections addressed' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
  pellworth: [
    { status: 'pass', evidence: 'Received 14 Aug 13:58 BST' },
    { status: 'pass', evidence: 'Pricing-Matrix rev. C, 41/41 lines' },
    { status: 'fail', note: 'No Cyber Essentials Plus certificate supplied. Basic Cyber Essentials only (cert IASME-CE-B-771204). Mandatory requirement M3 not met.', evidence: 'Response §4.1, p.22' },
    { status: 'fail', note: 'No ISO 27001 certificate and no equivalent assurance evidence supplied.', evidence: 'Absent from submission' },
    { status: 'pass', evidence: '£10m EL, Hiscox policy HX-9920-14' },
    { status: 'fail', note: 'Section 4 (Security & Information Governance) not addressed — 0 of 6 required sub-responses returned.', evidence: 'Response pp.20–23' },
    { status: 'pass', evidence: 'Self-declaration clean' },
  ],
};

export interface Citation { doc: string; page: number; clause: string; quote: string }
export interface ScoreEntry {
  sub: string;
  band: Band;
  confidence: number;
  rationale: string;
  strengths: string[];
  concerns: string[];
  evidence: Citation[];
}

/**
 * Assessor output, per compliant bidder, per sub-criterion. Bands are 0–4 on
 * the published scale; the engine converts band → weighted points.
 * Pellworth is absent by design: disqualified at the compliance gate.
 */
export const rawScores: Record<string, ScoreEntry[]> = {
  calderwood: [
    {
      sub: 'Q1.1', band: 4, confidence: 0.93,
      rationale: 'Transition plan is the most specific of the three, with a named transition manager, a 10-week phased cutover and explicit rollback gates at each phase.',
      strengths: ['Named transition manager with prior NDPB experience', 'Reverse-transition obligations priced in, not deferred'],
      concerns: ['Assumes Aldercroft supplies asset register within 5 working days of contract start'],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 14, clause: '§3.2.1', quote: 'Transition is delivered across four gated phases over 10 weeks, with a go/no-go review and documented rollback position at the close of each phase.' },
        { doc: 'Calderwood_Technical_Response.pdf', page: 17, clause: '§3.2.6', quote: 'A dedicated Transition Manager (Ms A. Verity, 11 years public sector) is assigned full-time for the duration of mobilisation and for 4 weeks post-cutover.' },
      ],
    },
    {
      sub: 'Q1.2', band: 4, confidence: 0.89,
      rationale: 'Strong automation story evidenced with named platforms and measured outcomes; the only bidder to quantify current automation coverage on a comparable estate.',
      strengths: ['62% of L1 tickets auto-resolved on a comparable 250-endpoint estate', 'Clear AI governance position referencing NCSC guidance'],
      concerns: ['Automation figures drawn from a single reference client'],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 23, clause: '§4.1.3', quote: 'On a comparable 250-endpoint hybrid estate we currently auto-resolve 62% of L1 tickets through runbook automation, measured over the 12 months to March 2026.' },
        { doc: 'Calderwood_Technical_Response.pdf', page: 26, clause: '§4.3', quote: 'AI-assisted triage operates under a human-in-the-loop policy; no automated action is taken on OFFICIAL-SENSITIVE data without analyst confirmation.' },
      ],
    },
    {
      sub: 'Q1.3', band: 3, confidence: 0.95,
      rationale: 'SLA framework meets the specification in full and reporting cadence is clear, but service credits are capped at a level below the two other compliant bidders.',
      strengths: ['P1 response 15 min / resolution 4 h, exceeding the specified target', 'Monthly service review with published KPI pack'],
      concerns: ['Service credits capped at 6% of monthly charge; Spec §3.4 anticipates up to 10%', 'Continuous-improvement targets stated qualitatively'],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 31, clause: '§5.2', quote: 'Priority 1 incidents carry a 15-minute response and 4-hour resolution target, measured 24/7/365.' },
        { doc: 'Calderwood_Technical_Response.pdf', page: 33, clause: '§5.4.2', quote: 'Aggregate service credits in any month shall not exceed 6% of the monthly Core Services charge.' },
      ],
    },
    {
      sub: 'Q2.1', band: 4, confidence: 0.97,
      rationale: 'Control set is mapped clause-by-clause to NCSC CAF objectives and evidenced with current certification; the mapping table is the strongest submitted.',
      strengths: ['Full CAF objective A–D mapping with control owners', 'CE+ and ISO 27001:2022 both current and in scope for the delivery entity'],
      concerns: [],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 44, clause: '§6.1', quote: 'Appendix C maps each of our 118 operational controls to NCSC CAF objectives A1–D2, naming the accountable control owner for each.' },
        { doc: 'Calderwood_Security_Annex.pdf', page: 4, clause: 'App. C', quote: 'ISO/IEC 27001:2022 certificate 2411-QMS-0117 covers managed service delivery from the Reading and Manchester operations centres.' },
      ],
    },
    {
      sub: 'Q2.2', band: 3, confidence: 0.88,
      rationale: 'Incident response is well documented with a 24/7 SOC, but the disaster-recovery evidence relies on a tabletop exercise rather than a live failover test.',
      strengths: ['24/7 UK-based SOC with SIEM integration', 'Defined severity matrix and escalation to Aldercroft SIRO'],
      concerns: ['Last full DR invocation test was tabletop only (Feb 2026); no live failover evidence'],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 51, clause: '§6.5', quote: 'Our UK-based SOC operates 24/7/365 with SIEM correlation and a documented severity matrix escalating Sev-1 events to the client SIRO within 30 minutes.' },
        { doc: 'Calderwood_Technical_Response.pdf', page: 54, clause: '§6.7.1', quote: 'The most recent business continuity exercise (February 2026) was conducted as a facilitated tabletop across the service management team.' },
      ],
    },
    {
      sub: 'Q3.1', band: 3, confidence: 0.91,
      rationale: 'Three relevant public-sector contracts of comparable scale with verifiable referees; scale is above Aldercroft rather than alongside it.',
      strengths: ['Three named public-sector referees, all contactable', 'Sustained 99.95% availability evidenced on the largest reference'],
      concerns: ['All references are 400+ user estates; none at Aldercroft’s ~120-user scale'],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 62, clause: '§7.1', quote: 'Reference 1: Greyfen Borough Council, 480 users, 5-year managed service, sustained 99.95% availability against a 99.9% target.' },
      ],
    },
    {
      sub: 'Q3.2', band: 3, confidence: 0.86,
      rationale: 'Team structure and certifications are strong, but named key personnel are committed at lower allocations than the other compliant bidders offer.',
      strengths: ['12 CCNP/AWS-certified engineers in the delivery pool', 'Documented skills matrix reviewed quarterly'],
      concerns: ['Service Delivery Manager committed at 0.4 FTE', 'macOS specialism evidenced for 3 of 12 engineers on a 70% macOS estate'],
      evidence: [
        { doc: 'Calderwood_Technical_Response.pdf', page: 68, clause: '§8.2', quote: 'The named Service Delivery Manager is allocated at 0.4 FTE, supported by a Service Desk Lead at 1.0 FTE.' },
      ],
    },
    {
      sub: 'Q4.1', band: 2, confidence: 0.84,
      rationale: 'Commitments are present and measurable but modest against the Schedule 7 outcomes, and SME subcontracting is the lowest of the three.',
      strengths: ['2 apprenticeships committed over the initial term'],
      concerns: ['SME spend committed at 8% against a 25% exemplar in Schedule 7', 'No local-employment commitment tied to the London delivery footprint'],
      evidence: [
        { doc: 'Calderwood_Social_Value.pdf', page: 3, clause: '§2.2', quote: 'We commit to two Level 3 apprenticeships across the initial 36-month term and to directing 8% of contract spend to SME subcontractors.' },
      ],
    },
    {
      sub: 'Q4.2', band: 2, confidence: 0.82,
      rationale: 'Net-zero position is stated with a credible target date but carbon reporting is not contract-specific, which Schedule 7 §2.4 asks for explicitly.',
      strengths: ['Organisation-level net zero by 2040 with published Carbon Reduction Plan'],
      concerns: ['No contract-level carbon baseline or reporting method offered', 'Device take-back described but not quantified'],
      evidence: [
        { doc: 'Calderwood_Social_Value.pdf', page: 7, clause: '§3.1', quote: 'Calderwood is committed to net zero by 2040 in line with our published Carbon Reduction Plan.' },
      ],
    },
  ],
  strathmere: [
    {
      sub: 'Q1.1', band: 3, confidence: 0.90,
      rationale: 'Credible, well-sequenced transition with sensible risk handling; less granular than Calderwood on gate criteria but appropriate to the estate size.',
      strengths: ['12-week transition sized to a 120-user estate', 'Knowledge-capture workshops scheduled with incumbent'],
      concerns: ['Rollback position described narratively rather than as defined gates'],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 3, clause: '§2.4', quote: 'Transition runs over 12 weeks in three workstreams — service desk, endpoint estate and security tooling — with a combined go-live review at week 10.' },
      ],
    },
    {
      sub: 'Q1.2', band: 3, confidence: 0.87,
      rationale: 'Solid automation and tooling coverage with a clear Google Workspace and MDM position; less quantified evidence of outcomes than Calderwood.',
      strengths: ['Native Google Workspace and Jamf expertise matched to the 70% macOS estate', 'Runbook library offered as a contract deliverable'],
      concerns: ['Automation benefit expressed as a target, not a measured baseline'],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 6, clause: '§3.3', quote: 'Endpoint management is delivered through Jamf Pro for macOS and Intune for Windows, with a shared compliance baseline reported into a single dashboard.' },
      ],
    },
    {
      sub: 'Q1.3', band: 4, confidence: 0.94,
      rationale: 'Best-in-set SLA construction: targets exceed the specification, credits are uncapped to 10%, and improvement targets are quantified year on year.',
      strengths: ['P1 response 10 min / resolution 3 h', 'Service credits to 10% of monthly charge, matching Spec §3.4', 'Committed 5% year-on-year ticket-volume reduction'],
      concerns: [],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 9, clause: '§4.2', quote: 'Priority 1: 10-minute response, 3-hour resolution. Service credits accrue to a maximum of 10% of the monthly Core Services charge.' },
        { doc: 'Strathmere_Technical_Response.pdf', page: 9, clause: '§4.4', quote: 'We commit to a 5% year-on-year reduction in inbound ticket volume through proactive problem management, reported quarterly and subject to service credit.' },
      ],
    },
    {
      sub: 'Q2.1', band: 3, confidence: 0.93,
      rationale: 'Controls meet the requirement with current certification; CAF alignment is asserted at objective level rather than mapped control by control.',
      strengths: ['CE+ and ISO 27001:2022 both current', 'RBAC, MFA and PAM described concretely'],
      concerns: ['CAF alignment summarised in prose; no control-level mapping table'],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 11, clause: '§5.1', quote: 'Our ISMS is certified to ISO/IEC 27001:2022 and aligned to the NCSC Cyber Assessment Framework across all four objectives.' },
      ],
    },
    {
      sub: 'Q2.2', band: 4, confidence: 0.92,
      rationale: 'Only bidder to evidence a live failover test within the last 12 months, with published recovery times against target — materially stronger assurance.',
      strengths: ['Live DR failover executed March 2026, RTO 3h 12m against a 4h target', 'Quarterly phishing simulation with reported click-through trend'],
      concerns: [],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 11, clause: '§5.6', quote: 'A live failover of the managed service platform was executed on 14 March 2026, achieving a measured RTO of 3 hours 12 minutes against a 4-hour target. The test report is provided at Appendix E.' },
        { doc: 'Strathmere_Security_Annex.pdf', page: 9, clause: 'App. E', quote: 'Quarterly simulated phishing campaigns reduced mean click-through from 14.2% to 4.6% across the reference client base over 18 months.' },
      ],
    },
    {
      sub: 'Q3.1', band: 3, confidence: 0.89,
      rationale: 'Two closely comparable NDPB contracts at similar scale; a third reference is commercial rather than public sector.',
      strengths: ['Two NDPB references at 100–160 users — closest scale match in the field'],
      concerns: ['Third reference is a private-sector engagement of limited relevance'],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 14, clause: '§6.1', quote: 'Reference 1: Marchmont Institute (NDPB), 140 users, cloud-first Google Workspace estate, contract running since 2023.' },
      ],
    },
    {
      sub: 'Q3.2', band: 3, confidence: 0.88,
      rationale: 'Well-matched team with strong macOS depth; overall pool is smaller than Calderwood’s, which constrains surge capacity.',
      strengths: ['Service Delivery Manager at 0.8 FTE', '6 of 9 named engineers hold current Jamf certification'],
      concerns: ['Total delivery pool of 9 limits resilience during concurrent absence'],
      evidence: [
        { doc: 'Strathmere_Technical_Response.pdf', page: 15, clause: '§7.2', quote: 'The Service Delivery Manager is committed at 0.8 FTE. Six of the nine named engineers hold current Jamf Certified Tech accreditation.' },
      ],
    },
    {
      sub: 'Q4.1', band: 4, confidence: 0.90,
      rationale: 'Commitments are specific, measurable and contractually bound with liquidated remedies — the only bidder to attach consequences to social value delivery.',
      strengths: ['4 apprenticeships and 2 Kickstart placements committed', '32% SME subcontract spend, exceeding the Schedule 7 exemplar', 'Commitments drafted as contractual KPIs with remedies'],
      concerns: [],
      evidence: [
        { doc: 'Strathmere_Social_Value.pdf', page: 4, clause: '§2.1', quote: 'We commit to four Level 3 apprenticeships and two Kickstart placements, and to directing 32% of contract spend to SMEs and VCSEs.' },
        { doc: 'Strathmere_Social_Value.pdf', page: 6, clause: '§2.3', quote: 'Each commitment is offered as a contractual KPI reported quarterly, with liquidated remedies where targets are missed for two consecutive quarters.' },
      ],
    },
    {
      sub: 'Q4.2', band: 4, confidence: 0.89,
      rationale: 'Contract-level carbon baseline and reporting method offered, which is exactly what Schedule 7 §2.4 requires and which no other bidder provided.',
      strengths: ['Contract-level carbon baseline with quarterly reporting', 'Net zero by 2035, ahead of the sector norm', '100% device refurbishment and take-back, quantified'],
      concerns: [],
      evidence: [
        { doc: 'Strathmere_Social_Value.pdf', page: 9, clause: '§3.2', quote: 'We will establish a contract-specific carbon baseline within 60 days of service commencement and report emissions quarterly against it using the GHG Protocol.' },
      ],
    },
  ],
  kelverton: [
    {
      sub: 'Q1.1', band: 3, confidence: 0.89,
      rationale: 'Methodical, well-governed transition with clear ownership at each stage; sized correctly for the estate but offers little beyond the requirement.',
      strengths: ['11-week transition with a named transition board', 'Incumbent knowledge-transfer sessions scheduled in week 2'],
      concerns: ['Rollback described at programme level rather than per phase'],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 12, clause: '§2.3', quote: 'Transition is governed by a fortnightly Transition Board chaired by the Service Delivery Manager, with formal stage-gate sign-off before each cutover.' },
      ],
    },
    {
      sub: 'Q1.2', band: 3, confidence: 0.86,
      rationale: 'Competent tooling with a credible macOS position; automation is described as delivered capability rather than aspiration, but is not quantified.',
      strengths: ['Jamf Pro and Google Workspace administration both evidenced in-house', 'Self-service catalogue offered from day one'],
      concerns: ['No automation coverage baseline supplied', 'AI adoption limited to vendor-supplied ITSM features'],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 20, clause: '§3.2', quote: 'A self-service catalogue covering the 20 highest-volume request types is delivered at go-live, with password reset, device enrolment and access requests fully automated.' },
      ],
    },
    {
      sub: 'Q1.3', band: 3, confidence: 0.92,
      rationale: 'SLA targets meet the specification and the reporting pack is well specified; service credits sit mid-field and improvement targets are directional.',
      strengths: ['P1 response 15 min / resolution 4 h', 'Monthly service pack with trend analysis and problem register'],
      concerns: ['Service credits capped at 7% of monthly charge', 'Improvement commitments not tied to measurable targets'],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 27, clause: '§4.1', quote: 'Priority 1 incidents carry a 15-minute response and 4-hour resolution target, with credits accruing to a maximum of 7% of the monthly Core Services charge.' },
      ],
    },
    {
      sub: 'Q2.1', band: 3, confidence: 0.94,
      rationale: 'Controls meet the requirement with current certification and a stated CAF position; the evidence is sound but not distinguishing.',
      strengths: ['CE+ and ISO 27001:2022 both current and in scope', 'MFA, RBAC and privileged access described concretely'],
      concerns: ['CAF alignment asserted at objective level without a control mapping'],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 35, clause: '§5.1', quote: 'All privileged access is brokered through a PAM solution with session recording and just-in-time elevation; standing administrative access is not permitted.' },
      ],
    },
    {
      sub: 'Q2.2', band: 3, confidence: 0.88,
      rationale: 'Incident handling is well documented with 24/7 cover; resilience testing is annual tabletop, meeting but not exceeding the requirement.',
      strengths: ['24/7 on-call with documented severity matrix', 'Quarterly security awareness campaign included'],
      concerns: ['No live failover evidence within the last 12 months'],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 41, clause: '§5.5', quote: 'Continuity arrangements are exercised annually through a facilitated tabletop, with findings tracked to closure in the service improvement register.' },
      ],
    },
    {
      sub: 'Q3.1', band: 3, confidence: 0.90,
      rationale: 'Three relevant public-sector contracts with contactable referees; scale is comparable but none is a research-sector or Google Workspace estate.',
      strengths: ['Three public-sector references at 150–300 users', 'All referees contactable and recently active'],
      concerns: ['No reference on a Google Workspace estate', 'No research-sector or NDPB comparator'],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 48, clause: '§6.1', quote: 'Reference 2: Ashcombe District Council, 210 users, managed service since 2022, Microsoft 365 estate with mixed Windows and macOS endpoints.' },
      ],
    },
    {
      sub: 'Q3.2', band: 4, confidence: 0.91,
      rationale: 'Strongest team commitment in the field: the highest named-personnel allocations, deputies named for every key role, and genuine surge capacity behind them.',
      strengths: ['Service Delivery Manager at 1.0 FTE — the only full-time commitment received', 'Named deputy for every key role at mobilisation', '240-strong organisation provides real surge capacity'],
      concerns: [],
      evidence: [
        { doc: 'Kelverton_Technical_Response.pdf', page: 54, clause: '§7.1', quote: 'The Service Delivery Manager is committed at 1.0 FTE and is dedicated to this contract. A named deputy is identified for each key role and is introduced during mobilisation.' },
        { doc: 'Kelverton_Technical_Response.pdf', page: 56, clause: '§7.3', quote: 'A further 18 engineers within the managed services division are security-cleared and available to surge onto the contract at 48 hours\u2019 notice.' },
      ],
    },
    {
      sub: 'Q4.1', band: 3, confidence: 0.87,
      rationale: 'Measurable commitments across employment and SME spend, benchmarked against the Schedule 7 exemplar but without contractual consequence.',
      strengths: ['3 apprenticeships and 1 supported internship', '24% SME and VCSE subcontract spend'],
      concerns: ['Commitments reported voluntarily rather than as contractual KPIs'],
      evidence: [
        { doc: 'Kelverton_Social_Value.pdf', page: 4, clause: '§2.1', quote: 'We commit to three apprenticeships and one supported internship over the initial term, and to directing 24% of contract spend to SME and VCSE suppliers.' },
      ],
    },
    {
      sub: 'Q4.2', band: 3, confidence: 0.88,
      rationale: 'Environmental commitments are credible and partially contract-specific, with a net zero date ahead of the field median.',
      strengths: ['Net zero by 2038 with a verified Carbon Reduction Plan', 'Device take-back with a quantified refurbishment rate'],
      concerns: ['Carbon reporting offered annually rather than quarterly'],
      evidence: [
        { doc: 'Kelverton_Social_Value.pdf', page: 8, clause: '§3.1', quote: 'We report contract-attributable emissions annually against a baseline established in the first quarter of service, and refurbish or reuse 92% of devices recovered.' },
      ],
    },
  ],
  marchford: [
    {
      sub: 'Q1.1', band: 2, confidence: 0.84,
      rationale: 'Transition is addressed but thinly. The plan is generic, milestones are not dated, and the resourcing behind it is not evidenced.',
      strengths: ['Phased approach described at a high level'],
      concerns: ['No dated milestone plan', 'Transition resource not named or quantified', 'Reverse-transition obligations not addressed'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 7, clause: '§2.1', quote: 'Our proven transition methodology moves through discovery, design, migration and stabilisation phases to ensure a smooth handover.' },
      ],
    },
    {
      sub: 'Q1.2', band: 2, confidence: 0.82,
      rationale: 'Tooling is adequate for the estate but the response does not engage with the cloud-first Google Workspace environment described in the specification.',
      strengths: ['Established ITSM platform with asset management'],
      concerns: ['Response assumes an on-premises Active Directory estate', 'No Google Workspace tooling described', 'No automation metrics offered'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 13, clause: '§3.1', quote: 'Endpoints are managed through Group Policy and SCCM, with domain-joined devices reporting to the central management server.' },
      ],
    },
    {
      sub: 'Q1.3', band: 3, confidence: 0.90,
      rationale: 'SLA targets meet the specification and are clearly tabulated — the strongest part of this submission.',
      strengths: ['P1 response 15 min / resolution 4 h', 'Clear SLA table with measurement definitions'],
      concerns: ['Service credits capped at 5%, the lowest offered'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 19, clause: '§4.2', quote: 'Priority 1: 15-minute response, 4-hour resolution, measured 24/7. Service credits accrue to a maximum of 5% of the monthly charge.' },
      ],
    },
    {
      sub: 'Q2.1', band: 3, confidence: 0.91,
      rationale: 'Certification is current and in scope, and the control narrative is proportionate — the one area where this tender is fully competitive.',
      strengths: ['CE+ and ISO 27001:2022 both current', 'Patch and vulnerability management described with cadence'],
      concerns: ['NCSC CAF not referenced'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 24, clause: '§5.1', quote: 'Critical vulnerabilities are remediated within 14 days and high within 30, verified by monthly authenticated scanning across the managed estate.' },
      ],
    },
    {
      sub: 'Q2.2', band: 2, confidence: 0.85,
      rationale: 'Incident response is described at policy level only. There is no evidence of 24/7 coverage for the critical incident cover the specification requires.',
      strengths: ['Documented incident management policy'],
      concerns: ['Support hours stated as 07:00–19:00 with out-of-hours on a best-endeavours basis', 'Spec §4.5 requires 24/7 critical incident coverage', 'No SIEM or centralised logging offered'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 29, clause: '§5.4', quote: 'Standard support hours are 07:00 to 19:00 Monday to Friday. Out-of-hours incidents are handled on a best-endeavours basis via the on-call mobile.' },
      ],
    },
    {
      sub: 'Q3.1', band: 2, confidence: 0.83,
      rationale: 'Two references are supplied but neither is closely comparable in scale, sector or technology, and one is more than four years old.',
      strengths: ['Two contactable referees provided'],
      concerns: ['Largest reference is 60 users against an estate of ~120', 'One reference concluded in 2022', 'No public-sector reference at comparable scale'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 33, clause: '§6.1', quote: 'Reference 1: Wexley Chambers, 60 users, IT support contract concluded in 2022.' },
      ],
    },
    {
      sub: 'Q3.2', band: 2, confidence: 0.84,
      rationale: 'The named team is small relative to the estate and carries a single point of dependency; no deputies are identified.',
      strengths: ['Named engineers hold relevant Microsoft certifications'],
      concerns: ['Delivery pool of 5 for a 250-endpoint estate', 'Service Delivery Manager at 0.2 FTE and shared across four contracts', 'No macOS certification evidenced on a 70% macOS estate'],
      evidence: [
        { doc: 'Marchford_Technical_Response.pdf', page: 38, clause: '§7.1', quote: 'The Service Delivery Manager allocates approximately one day per week to each of the contracts within their portfolio.' },
      ],
    },
    {
      sub: 'Q4.1', band: 3, confidence: 0.86,
      rationale: 'Genuinely local and specific commitments — proportionate to the size of the organisation and among the better social value responses received.',
      strengths: ['2 apprenticeships committed against a 72-person headcount', '30% SME spend, exceeding the Schedule 7 exemplar', 'Partnership with a Sheffield employment charity named'],
      concerns: ['No supported-internship or care-leaver commitment'],
      evidence: [
        { doc: 'Marchford_Social_Value.pdf', page: 3, clause: '§2.1', quote: 'We commit to two apprenticeships over the initial term and to directing 30% of contract spend to SME suppliers, working with Sheffield Works to source candidates.' },
      ],
    },
    {
      sub: 'Q4.2', band: 2, confidence: 0.81,
      rationale: 'Environmental commitments are organisation-level and largely restate regulatory baselines, with no contract-specific measurement.',
      strengths: ['WEEE-compliant disposal through an accredited partner'],
      concerns: ['No carbon baseline or reporting offered', 'No net zero target date stated'],
      evidence: [
        { doc: 'Marchford_Social_Value.pdf', page: 6, clause: '§3.1', quote: 'All redundant equipment is disposed of through a licensed WEEE partner in line with our environmental policy.' },
      ],
    },
  ],
  havelock: [
    {
      sub: 'Q1.1', band: 3, confidence: 0.85,
      rationale: 'Transition approach is sound and appropriately scaled, though dependencies on Aldercroft resource are heavier than the other bidders assume.',
      strengths: ['14-week transition with weekly checkpoint governance'],
      concerns: ['Requires 0.5 FTE of Aldercroft technical resource throughout transition'],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 9, clause: '§2.2', quote: 'Transition assumes the availability of a nominated Aldercroft technical contact at approximately 0.5 FTE for the full 14-week period.' },
      ],
    },
    {
      sub: 'Q1.2', band: 2, confidence: 0.81,
      rationale: 'Tooling is adequate but the automation and AI response is largely generic, with little that is specific to a cloud-first Google Workspace estate.',
      strengths: ['Standard ITSM platform with self-service portal'],
      concerns: ['No Google Workspace specific tooling described', 'AI section reads as capability marketing without applied examples', 'No automation coverage metrics offered'],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 16, clause: '§3.1', quote: 'We leverage best-of-breed AI and automation technologies to drive efficiency across the service lifecycle.' },
      ],
    },
    {
      sub: 'Q1.3', band: 3, confidence: 0.91,
      rationale: 'SLA targets meet the specification and reporting is adequate; nothing exceeds requirement.',
      strengths: ['P1 response 15 min / resolution 4 h', 'Service credits to 8% of monthly charge'],
      concerns: ['Continuous improvement described without measurable targets'],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 22, clause: '§4.1', quote: 'Priority 1 incidents are responded to within 15 minutes and resolved within 4 hours, with credits accruing to 8% of the monthly charge.' },
      ],
    },
    {
      sub: 'Q2.1', band: 2, confidence: 0.83,
      rationale: 'Meets the requirement only once the recertification evidence accepted at compliance is taken into account; the underlying control narrative is thin.',
      strengths: ['CE+ current to November 2026', 'UKAS recertification audit accepted as equivalent'],
      concerns: ['ISO 27001 certificate lapsed May 2026, recertification not yet complete', 'CAF alignment not addressed', 'CE+ expires two months into the contract term'],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 30, clause: '§5.1', quote: 'Our ISO 27001 recertification audit was completed on 22 July 2026; the certificate is expected to issue in Q4 2026.' },
      ],
    },
    {
      sub: 'Q2.2', band: 3, confidence: 0.86,
      rationale: 'Incident response is well described with clear severity handling; resilience testing is annual tabletop, consistent with the requirement but not beyond it.',
      strengths: ['Documented severity matrix and 24/7 on-call rota'],
      concerns: ['Business continuity tested annually by tabletop only'],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 36, clause: '§5.4', quote: 'Business continuity arrangements are exercised annually through a facilitated tabletop involving service management and technical leads.' },
      ],
    },
    {
      sub: 'Q3.1', band: 4, confidence: 0.90,
      rationale: 'Strongest reference set in the field: four directly comparable research-sector contracts, including two at almost identical scale and technology profile.',
      strengths: ['Four research-sector references, two at 110–130 users', 'Google Workspace estate on three of four references'],
      concerns: [],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 42, clause: '§6.1', quote: 'Reference 2: Thornbury Research Council, 118 users, cloud-first Google Workspace estate with Jamf-managed macOS fleet — closely comparable to the Aldercroft environment.' },
      ],
    },
    {
      sub: 'Q3.2', band: 3, confidence: 0.84,
      rationale: 'Team is credible and appropriately certified; key-personnel commitments sit between the other two bidders.',
      strengths: ['Service Delivery Manager at 0.6 FTE', 'Named macOS specialist assigned'],
      concerns: ['Smallest organisation in the field; single point of dependency on the named architect'],
      evidence: [
        { doc: 'Havelock_Technical_Response.pdf', page: 48, clause: '§7.1', quote: 'The Lead Architect (Mr D. Rowntree) is the named technical authority for this contract at 0.3 FTE.' },
      ],
    },
    {
      sub: 'Q4.1', band: 3, confidence: 0.87,
      rationale: 'Good, locally grounded commitments with measurable targets, though below Strathmere on both apprenticeships and SME spend.',
      strengths: ['3 apprenticeships committed', '21% SME subcontract spend', 'Partnership with a Bristol digital-skills charity'],
      concerns: ['Commitments not tied to contractual remedies'],
      evidence: [
        { doc: 'Havelock_Social_Value.pdf', page: 3, clause: '§2.1', quote: 'We commit to three apprenticeships over the initial term and to directing 21% of contract spend to SME and VCSE suppliers.' },
      ],
    },
    {
      sub: 'Q4.2', band: 2, confidence: 0.80,
      rationale: 'Environmental commitments are stated at organisation level only, with no contract-level measurement offered.',
      strengths: ['Net zero by 2045 with a published Carbon Reduction Plan'],
      concerns: ['No contract-level carbon baseline', 'Net zero target date is the latest in the field'],
      evidence: [
        { doc: 'Havelock_Social_Value.pdf', page: 6, clause: '§3.1', quote: 'Havelock Digital Group is committed to achieving net zero across our operations by 2045.' },
      ],
    },
  ],
};

/** Cross-submission integrity checks the assessors would not catch by reading. */
export const anomalies = [
  {
    id: 'A1', severity: 'high' as const, title: 'Near-duplicate text across two submissions',
    detail: 'Havelock §5.4 and Pellworth §5.2 share 94.1% lexical overlap across 1,180 words, including three identical transposition errors. Consistent with common authorship or a shared bid-writing supplier.',
    affects: ['havelock', 'pellworth'],
    action: 'Referred to the Procurement Lead. Clarification issued under ITT §9.2; both bidders confirmed use of the same external bid consultancy. No collusion finding — recorded on the audit trail.',
    status: 'resolved' as const,
  },
  {
    id: 'A2', severity: 'medium' as const, title: 'Tendered price materially above pre-tender estimate',
    detail: 'Calderwood at £3.48m sits 14.1% above the £3.05m pre-tender estimate; Havelock at £3.69m sits 21.0% above. Both remain within the affordability ceiling of £3.75m.',
    affects: ['calderwood', 'havelock'],
    action: 'Noted. No action required — both within the published affordability ceiling.',
    status: 'noted' as const,
  },
  {
    id: 'A3', severity: 'medium' as const, title: 'Unevidenced performance claim',
    detail: 'Calderwood §4.1.3 cites a 62% L1 auto-resolution rate on a comparable estate but supplies a single reference and no measurement method.',
    affects: ['calderwood'],
    action: 'Clarification issued 19 Aug; measurement method supplied and accepted. Band held at 4 with the concern recorded.',
    status: 'resolved' as const,
  },
  {
    id: 'A5', severity: 'high' as const, title: 'Tender received after the deadline',
    detail: 'Brayford uploaded at 14:11:38 BST on 14 August, 11 minutes and 38 seconds after the 14:00 deadline. Portal logs show the upload commenced at 13:58:04 and completed after the cut-off.',
    affects: ['brayford'],
    action: 'Excluded under ITT §4.1. The portal completion timestamp is determinative; a late tender cannot be considered irrespective of when the upload began. Supplier notified 17 Aug.',
    status: 'resolved' as const,
  },
  {
    id: 'A6', severity: 'medium' as const, title: 'Response inconsistent with the specified environment',
    detail: 'Marchford §3.1 describes management of a domain-joined estate via Group Policy and SCCM. Spec §2.4 states a cloud-first estate with no legacy infrastructure and 70% macOS endpoints.',
    affects: ['marchford'],
    action: 'Clarification issued 19 Aug. Supplier confirmed the section was drawn from a template and did not amend the technical proposal. Scored as submitted — the panel may not credit capability that was not offered.',
    status: 'resolved' as const,
  },
  {
    id: 'A7', severity: 'medium' as const, title: 'Reference contract only partly corroborated',
    method: 'web-research' as const,
    sources: ['Contracts Finder award notices', 'Find a Tender', 'Greyfen Borough Council publications'],
    detail: 'Calderwood §7.1 cites Greyfen Borough Council as a "five-year managed service". The published award notice was located on Contracts Finder and matched to the bidder by Companies House number 09482113. It records a contract of 36 months with a 24-month extension option, awarded 2021 at £1.42m — consistent with five years only if the extension was exercised, which the notice alone does not evidence.',
    affects: ['calderwood'],
    action: 'Clarification issued 20 Aug. Supplier provided the extension variation and the referee confirmed continuous service to 2026. Claim accepted; the corroborating notice is filed with the assessment record.',
    status: 'resolved' as const,
  },
  {
    id: 'A8', severity: 'medium' as const, title: 'Certificate and register validation across all tenders',
    method: 'external-api' as const,
    sources: ['Companies House Public Data API', 'IASME / NCSC Cyber Essentials certificate search'],
    detail: 'All 7 suppliers checked against the company register and the certificate register. Three findings: Pellworth holds no Cyber Essentials Plus certificate on the IASME register — only Cyber Essentials Basic — which independently corroborates the M3 failure rather than relying on the submission alone. Havelock\u2019s CE Plus expires 30 November 2026, two months into the contract term. Havelock and Pellworth share no officers and no persons with significant control, which supports the shared bid-consultancy explanation at A1 rather than a collusion finding.',
    affects: ['pellworth', 'havelock'],
    action: 'M3 determination upheld for Pellworth on independent evidence. Havelock certificate expiry carried to the risk register as a condition of award. No further action on the A1 relationship. Registers do not publish structured turnover, so the CP1 turnover condition remains evidenced by the filed accounts supplied with the tender.',
    status: 'resolved' as const,
  },
  {
    id: 'A4', severity: 'low' as const, title: 'Scoring divergence between assessors',
    detail: 'Six divergences across 45 assessments, of which one exceeded a single band: Havelock Q2.1, scored 3 by AS2 and 1 by AS3. The remaining five were within one band.',
    affects: ['havelock'],
    action: 'All six resolved at moderation on 24 Aug with joint rationale recorded against each. Havelock Q2.1 moderated to band 2.',
    status: 'resolved' as const,
  },
];

/** Streaming agent pipelines. `t` is elapsed seconds within the run. */
export interface AgentLine { t: number; agent: string; text: string; kind?: 'ok' | 'warn' | 'info' }
export interface AgentDef { id: string; name: string; detail: string; until: number }

export const issuerAgents: AgentDef[] = [
  { id: 'ingest', name: 'Document Ingestion', detail: 'OCR, layout parse, section segmentation', until: 18.5 },
  { id: 'criteria', name: 'Criteria Extraction', detail: 'Award criteria, sub-criteria and weightings', until: 38.0 },
  { id: 'method', name: 'Methodology Inference', detail: 'Scoring scale, price model, thresholds', until: 52.0 },
  { id: 'compliance', name: 'Regulatory Check', detail: 'Procurement Act 2023 and PPN conformance', until: 66.0 },
];

export const issuerLog: AgentLine[] = [
  { t: 0.4, agent: 'ingest', text: 'Opening tender package · chain of custody recorded' },
  { t: 1.6, agent: 'ingest', text: 'ARA-2026-ITS-014_ITT.pdf — 18 pages, text layer present' },
  { t: 3.1, agent: 'ingest', text: 'ITT segmented into 246 blocks · 14 tables · 12 sections' },
  { t: 4.4, agent: 'ingest', text: 'Heading hierarchy resolved to 6 levels — §1 through §11' },
  { t: 5.8, agent: 'ingest', text: 'Schedule-2_Specification.pdf — 41 pages, 218 blocks' },
  { t: 7.0, agent: 'ingest', text: 'Requirement language detected: 96 "shall", 34 "must", 51 "should"' },
  { t: 8.3, agent: 'ingest', text: 'Schedule-4_Evaluation-Model.xlsx — 3 sheets, 6 printable pages' },
  { t: 9.5, agent: 'ingest', text: 'Weighting matrix located on Tab 2, range B4:F19', kind: 'ok' },
  { t: 10.8, agent: 'ingest', text: 'Schedule-7_Social-Value.pdf — 12 pages' },
  { t: 12.0, agent: 'ingest', text: 'Pages 9–12 have no text layer → OCR fallback engaged', kind: 'warn' },
  { t: 13.9, agent: 'ingest', text: 'OCR complete · 4 pages · mean character confidence 0.981', kind: 'ok' },
  { t: 15.1, agent: 'ingest', text: 'Pricing-Matrix_v3.xlsx — 41 priced lines across 2 sheets' },
  { t: 16.4, agent: 'ingest', text: 'Cross-referencing internal citations · 118 resolved, 0 broken', kind: 'ok' },
  { t: 17.6, agent: 'ingest', text: 'Building page-anchored index for citation traceability' },
  { t: 18.3, agent: 'ingest', text: 'Ingestion complete · all pages indexed', kind: 'ok' },

  { t: 19.4, agent: 'criteria', text: 'Scanning for award criteria across all documents' },
  { t: 20.8, agent: 'criteria', text: 'Candidate block — ITT §5.3, p.22 "Award criteria and weightings"' },
  { t: 22.1, agent: 'criteria', text: 'Confirmed: table of 5 criteria with stated percentage weightings', kind: 'ok' },
  { t: 23.5, agent: 'criteria', text: 'Q1 Technical Approach & Service Delivery — 22% · confidence 0.97' },
  { t: 24.7, agent: 'criteria', text: 'Q2 Security & Information Governance — 16% · confidence 0.98' },
  { t: 25.9, agent: 'criteria', text: 'Q3 Experience & Capability — 12% · confidence 0.93' },
  { t: 27.1, agent: 'criteria', text: 'Q4 Social Value — 10% · confidence 0.91' },
  { t: 28.3, agent: 'criteria', text: 'P1 Price — 40% · confidence 0.99' },
  { t: 29.6, agent: 'criteria', text: 'Quality weightings sum to 60, price 40 — total 100', kind: 'ok' },
  { t: 31.0, agent: 'criteria', text: 'Descending into sub-criteria · Schedule-2 §2, §3, §4' },
  { t: 32.3, agent: 'criteria', text: 'Q1 → Q1.1 service model 8%, Q1.2 toolchain 7%, Q1.3 SLA 7%' },
  { t: 33.5, agent: 'criteria', text: 'Q2 → Q2.1 cyber controls 8%, Q2.2 incident response 8%' },
  { t: 34.6, agent: 'criteria', text: 'Q3 → Q3.1 comparable contracts 7%, Q3.2 team & capacity 5%' },
  { t: 35.8, agent: 'criteria', text: 'Q4 → Q4.1 local skills 5%, Q4.2 environmental 5% (Schedule-7 §2)' },
  { t: 36.9, agent: 'criteria', text: 'Sub-criteria reconciled against Schedule-4 Tab 2 — consistent', kind: 'ok' },
  { t: 37.7, agent: 'criteria', text: '14 criteria nodes extracted · every node page-anchored', kind: 'ok' },

  { t: 38.8, agent: 'method', text: 'Searching for the scoring methodology' },
  { t: 40.2, agent: 'method', text: 'Scoring scale located — ITT §5.7, p.10' },
  { t: 41.5, agent: 'method', text: '0–4 banded scale with written definitions for each band', kind: 'ok' },
  { t: 42.8, agent: 'method', text: 'Band → points conversion inferred: band ÷ 4 × sub-criterion weight' },
  { t: 44.1, agent: 'method', text: 'Price model — ITT §6.2: lowest compliant price ÷ tendered price × 40' },
  { t: 45.4, agent: 'method', text: 'Affordability ceiling detected: £3,750,000' },
  { t: 46.6, agent: 'method', text: 'Quality gateway detected: 30 of 60 available quality points' },
  { t: 47.9, agent: 'method', text: 'Extracting mandatory requirements from Schedule-2 §4 and ITT §7' },
  { t: 49.2, agent: 'method', text: '7 mandatory requirements resolved to pass/fail gates', kind: 'ok' },
  { t: 50.4, agent: 'method', text: '3 tie-break rules captured — ITT §5.9' },
  { t: 51.5, agent: 'method', text: 'Disqualification triggers captured — 4 conditions' },

  { t: 52.7, agent: 'compliance', text: 'Testing the model against the declared regime' },
  { t: 54.1, agent: 'compliance', text: 'Award basis is MAT — correct for a Procurement Act 2023 procurement', kind: 'ok' },
  { t: 55.6, agent: 'compliance', text: 'MEAT language not present — no legacy PCR 2015 wording found', kind: 'ok' },
  { t: 57.0, agent: 'compliance', text: 'Social value weighted at 10% — meets the PPN 002 minimum', kind: 'ok' },
  { t: 58.4, agent: 'compliance', text: 'Sub-criteria sums verified against every parent weighting', kind: 'ok' },
  { t: 59.8, agent: 'compliance', text: 'Scoring scale published with definitions — transparency satisfied', kind: 'ok' },
  { t: 61.1, agent: 'compliance', text: 'Assessment summary fields derivable for all 5 criteria', kind: 'ok' },
  { t: 62.5, agent: 'compliance', text: 'Standstill obligation noted — 8 working days from award notice' },
  { t: 63.8, agent: 'compliance', text: 'No unweighted or undefined criterion detected', kind: 'ok' },
  { t: 65.0, agent: 'compliance', text: 'Model is publishable · 14 nodes · mean confidence 0.94', kind: 'ok' },
];

export const scoringAgents: AgentDef[] = [
  { id: 'intake', name: 'Submission Intake', detail: 'Parse, hash, integrity check', until: 14.0 },
  { id: 'map', name: 'Requirement Mapping', detail: 'Response sections → criteria', until: 30.0 },
  { id: 'gate', name: 'Compliance Gate', detail: 'Mandatory requirements and exclusion grounds', until: 44.0 },
  { id: 'assess', name: 'Evidence Assessment', detail: 'Band, cite and justify each sub-criterion', until: 68.0 },
  { id: 'integrity', name: 'Integrity & Anomaly', detail: 'Cross-submission, register and public-record checks', until: 89.0 },
];

export const scoringLog: AgentLine[] = [
  { t: 0.5, agent: 'intake', text: 'Opening 7 submissions · 41 documents · 1,343 pages' },
  { t: 2.0, agent: 'intake', text: 'SHA-256 recorded for each submission — chain of custody opened', kind: 'ok' },
  { t: 3.4, agent: 'intake', text: 'Calderwood Technologies Ltd — 6 documents, 214 pages' },
  { t: 4.7, agent: 'intake', text: 'Strathmere Solutions Ltd — 8 documents, 268 pages' },
  { t: 6.0, agent: 'intake', text: 'Kelverton Managed Services Ltd — 7 documents, 231 pages' },
  { t: 7.2, agent: 'intake', text: 'Havelock Digital Group — 5 documents, 176 pages' },
  { t: 8.4, agent: 'intake', text: 'Marchford Systems Ltd — 5 documents, 149 pages' },
  { t: 9.6, agent: 'intake', text: 'Brayford Technology Partners Ltd — 6 documents, 187 pages' },
  { t: 10.8, agent: 'intake', text: 'Pellworth IT Services — 4 documents, 118 pages' },
  { t: 11.9, agent: 'intake', text: 'Portal timestamps verified against the 14 Aug 14:00 BST deadline' },
  { t: 12.8, agent: 'intake', text: 'Brayford received 14:11:38 BST — 11m 38s late', kind: 'warn' },
  { t: 13.6, agent: 'intake', text: 'Pellworth is 118 pages — 45% below field median', kind: 'warn' },

  { t: 15.0, agent: 'map', text: 'Mapping 1,343 pages onto 9 sub-criteria' },
  { t: 16.6, agent: 'map', text: 'Building section embeddings per submission' },
  { t: 18.2, agent: 'map', text: 'Calderwood · 9/9 matched · mean relevance 0.86', kind: 'ok' },
  { t: 19.8, agent: 'map', text: 'Strathmere · 9/9 matched · mean relevance 0.88', kind: 'ok' },
  { t: 21.3, agent: 'map', text: 'Kelverton · 9/9 matched · mean relevance 0.84', kind: 'ok' },
  { t: 22.8, agent: 'map', text: 'Havelock · 9/9 matched · mean relevance 0.79', kind: 'ok' },
  { t: 24.2, agent: 'map', text: 'Marchford · 9/9 matched · mean relevance 0.71 — lowest in field', kind: 'warn' },
  { t: 25.6, agent: 'map', text: 'Brayford · 9/9 matched · mean relevance 0.83', kind: 'ok' },
  { t: 27.0, agent: 'map', text: 'Pellworth · 6/9 matched · Q1.3, Q2.1 and Q2.2 unmatched', kind: 'warn' },
  { t: 28.4, agent: 'map', text: 'Unmatched sections flagged for the compliance gate' },
  { t: 29.5, agent: 'map', text: 'Mapping complete · 4,180 candidate passages retained', kind: 'ok' },

  { t: 31.0, agent: 'gate', text: 'Evaluating 7 mandatory requirements across 7 tenders' },
  { t: 32.6, agent: 'gate', text: 'M1 receipt before deadline — Brayford fails', kind: 'warn' },
  { t: 34.0, agent: 'gate', text: 'Brayford disregarded under ITT §4.1 — a late tender cannot be considered', kind: 'warn' },
  { t: 35.4, agent: 'gate', text: 'M2 pricing matrix — Marchford submitted on superseded template rev. B', kind: 'warn' },
  { t: 36.6, agent: 'gate', text: 'Marchford rev. C resubmitted 18 Aug, figures identical — accepted', kind: 'ok' },
  { t: 37.9, agent: 'gate', text: 'M3 Cyber Essentials Plus — Pellworth holds Basic only', kind: 'warn' },
  { t: 39.1, agent: 'gate', text: 'M4 ISO 27001 — Havelock certificate lapsed 31 May 2026' },
  { t: 40.2, agent: 'gate', text: 'Havelock UKAS recertification audit accepted as equivalent', kind: 'ok' },
  { t: 41.3, agent: 'gate', text: 'M5 insurance — Strathmere certificate names the parent entity', kind: 'warn' },
  { t: 42.4, agent: 'gate', text: 'M6 mandatory sections — Pellworth Section 4 absent', kind: 'warn' },
  { t: 43.4, agent: 'gate', text: 'Determination: 5 proceed, 2 excluded (Pellworth, Brayford)', kind: 'ok' },

  { t: 45.0, agent: 'assess', text: 'Assessing 5 compliant tenders × 9 sub-criteria = 45 assessments' },
  { t: 46.8, agent: 'assess', text: 'Calderwood Q1 — extracting evidence for service model, toolchain, SLA' },
  { t: 48.6, agent: 'assess', text: 'Calderwood Q2 — CAF control mapping located, Appendix C p.44' },
  { t: 50.3, agent: 'assess', text: 'Calderwood assessed · 14 citations · mean confidence 0.90', kind: 'ok' },
  { t: 52.1, agent: 'assess', text: 'Strathmere Q1.3 — service credits to 10%, matches Spec §3.4' },
  { t: 53.8, agent: 'assess', text: 'Strathmere Q2.2 — live failover test evidenced, RTO 3h 12m' },
  { t: 55.4, agent: 'assess', text: 'Strathmere assessed · 13 citations · mean confidence 0.90', kind: 'ok' },
  { t: 57.2, agent: 'assess', text: 'Kelverton Q3.2 — 1.0 FTE service delivery manager, deputies named' },
  { t: 58.9, agent: 'assess', text: 'Kelverton assessed · 10 citations · mean confidence 0.89', kind: 'ok' },
  { t: 60.6, agent: 'assess', text: 'Havelock Q2.1 — recertification evidence weighed against lapsed cert' },
  { t: 62.2, agent: 'assess', text: 'Havelock assessed · 9 citations · mean confidence 0.85', kind: 'ok' },
  { t: 63.9, agent: 'assess', text: 'Marchford Q2.2 — support hours 07:00–19:00 against a 24/7 requirement', kind: 'warn' },
  { t: 65.5, agent: 'assess', text: 'Marchford assessed · 9 citations · mean confidence 0.85', kind: 'ok' },
  { t: 67.0, agent: 'assess', text: '45 assessments complete · every band page-anchored', kind: 'ok' },

  { t: 68.6, agent: 'integrity', text: 'Running cross-submission comparison on 4,180 passages' },
  { t: 70.3, agent: 'integrity', text: 'Havelock §5.4 ↔ Pellworth §5.2 — 94.1% lexical overlap', kind: 'warn' },
  { t: 71.8, agent: 'integrity', text: 'Three identical transposition errors — consistent with common authorship', kind: 'warn' },
  { t: 73.2, agent: 'integrity', text: 'Marchford §3.1 assumes on-premises AD — inconsistent with Spec §2.4', kind: 'warn' },
  { t: 74.6, agent: 'integrity', text: 'Calderwood §4.1.3 — 62% automation claim carries a single reference', kind: 'warn' },
  { t: 76.0, agent: 'integrity', text: 'Price spread £2.94m–£3.69m against a £3.05m estimate' },
  { t: 77.0, agent: 'integrity', text: 's.19 abnormally low test — not triggered for any tender', kind: 'ok' },
  { t: 78.0, agent: 'integrity', text: 'Querying Companies House for status, officers and PSC · 7 suppliers' },
  { t: 79.4, agent: 'integrity', text: 'All 7 active on the register · none in liquidation or administration', kind: 'ok' },
  { t: 80.6, agent: 'integrity', text: 'Havelock ↔ Pellworth — no shared officers or PSC', kind: 'ok' },
  { t: 81.9, agent: 'integrity', text: 'IASME certificate register — Pellworth holds CE Basic, not CE Plus', kind: 'warn' },
  { t: 83.1, agent: 'integrity', text: 'Havelock CE Plus expires 30 Nov 2026 — two months into the term', kind: 'warn' },
  { t: 84.4, agent: 'integrity', text: 'Searching Contracts Finder for the cited reference contracts' },
  { t: 85.8, agent: 'integrity', text: 'Greyfen award notice matched on company number 09482113' },
  { t: 87.1, agent: 'integrity', text: 'Notice records 36 months + 24 option against a "five-year" claim', kind: 'warn' },
  { t: 88.3, agent: 'integrity', text: 'Assessment complete · 55 citations · 8 flags raised', kind: 'ok' },
];

export const auditTrail = [
  { ts: '2026-08-17T09:04:00Z', actor: 'System', event: 'Evaluation opened for ARA-2026-ITS-014', detail: 'Panel of 3 constituted' },
  { ts: '2026-08-17T09:11:00Z', actor: 'System', event: 'Submissions sealed and hashed', detail: '7 submissions, SHA-256 recorded' },
  { ts: '2026-08-17T11:26:00Z', actor: 'r.awoyemi@aldercroft.gov.uk', event: 'Late tender determination', detail: 'Brayford excluded — received 11m 38s after deadline; supplier notified' },
  { ts: '2026-08-18T14:22:00Z', actor: 'r.awoyemi@aldercroft.gov.uk', event: 'Clarification issued', detail: 'Strathmere — M5 insurance entity name' },
  { ts: '2026-08-19T10:47:00Z', actor: 'r.awoyemi@aldercroft.gov.uk', event: 'Clarification issued', detail: 'Calderwood — measurement method for §4.1.3 claim' },
  { ts: '2026-08-19T15:12:00Z', actor: 'r.awoyemi@aldercroft.gov.uk', event: 'Clarification issued', detail: 'Marchford — pricing template revision and on-premises AD inconsistency' },
  { ts: '2026-08-21T09:58:00Z', actor: 'd.okonkwo@aldercroft.gov.uk', event: 'Clarification received', detail: 'Kelverton — security clearance evidence for surge pool' },
  { ts: '2026-08-20T16:03:00Z', actor: 'Panel', event: 'Compliance determination recorded', detail: 'Pellworth excluded — M3, M4, M6; Brayford excluded — M1' },
  { ts: '2026-08-24T11:30:00Z', actor: 'Panel', event: 'Moderation held', detail: 'Havelock Q2.1 and Marchford Q2.2 divergences resolved to band 2' },
  { ts: '2026-08-25T08:15:00Z', actor: 'System', event: 'Assessment run completed', detail: '45 assessments, 55 citations' },
];

export const ittEvidence = [
  { criterion: 'Q1', signal: 'Cloud-first estate, no legacy infrastructure; ~120 users / ~250 endpoints, 70% macOS', source: 'ITT §2.4, p.3' },
  { criterion: 'Q1', signal: 'Transition must include reverse-transition obligations priced at tender', source: 'Spec §2.1, p.9' },
  { criterion: 'Q2', signal: 'Cyber Essentials Plus mandatory; NCSC CAF alignment and OFFICIAL-SENSITIVE handling', source: 'Spec §4.1, p.26' },
  { criterion: 'Q2', signal: 'Centralised monitoring/logging (SIEM), 24/7 critical incident coverage', source: 'Spec §4.5, p.30' },
  { criterion: 'Q3', signal: 'Public-sector references with SLAs/KPIs; comparable scale preferred', source: 'ITT §5.5.1, p.10' },
  { criterion: 'Q4', signal: 'Social value scored against the PPN 002 model; contract-level carbon reporting expected', source: 'Sch. 7 §2.4, p.8' },
  { criterion: 'P1', signal: 'Core services fixed monthly fee; additional services on rate card; £3.75m affordability ceiling', source: 'ITT §6.2, p.12' },
];

/* ==========================================================================
 * Schema-faithful layer
 * --------------------------------------------------------------------------
 * Field names below mirror src/open_tender/models.py so the workspace can be
 * pointed at the real graph output without a translation layer:
 *   ScoringMethodology · EvaluationCriterion · ComplianceRequirement
 *   SubmissionRequirement · RiskFactor · SourceProvenance · ConfidenceScore
 * ======================================================================== */

export type ConfidenceLevel = 'low' | 'medium' | 'high';
export type ScoringMethod = 'points' | 'percentage' | 'weighted' | 'pass_fail' | 'qualitative';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type ExtractionMethod = 'llm' | 'langextract';

export const confidenceLevel = (v: number): ConfidenceLevel => (v >= 0.9 ? 'high' : v >= 0.75 ? 'medium' : 'low');

/** models.py :: SourceProvenance — character-level anchor for audit. */
export interface SourceProvenance {
  char_start: number;
  char_end: number;
  page_number: number;
  source_text: string;
  alignment_status: 'aligned' | 'partial' | 'inferred';
}

/** models.py :: ScoringMethodology */
export const scoringMethodology = {
  point_scale: [0, 4] as [number, number],
  scoring_method: 'weighted' as ScoringMethod,
  normalization_applied: true,
  minimum_qualifying_score: 30,
  thresholds: {
    quality_gateway: 30,
    sub_criterion_floor: 2,
    affordability_ceiling: 3_750_000,
  },
  tie_break_rules: [
    'Highest quality score prevails where total scores are equal.',
    'Where quality is also equal, the highest score on Q2 Security & Information Governance prevails.',
    'Where those are equal, the lower tendered price prevails.',
  ],
  disqualification_triggers: [
    'A band of 0 or 1 awarded on any sub-criterion.',
    'Total quality score below the 30-point quality gateway.',
    'Failure of any mandatory requirement at the compliance gate.',
    'Tendered price above the £3.75m affordability ceiling.',
  ],
  price_formula: 'lowest_compliant_price / tendered_price * price_weight',
  abnormally_low_threshold_pct: 15,
};

/** models.py :: SubmissionRequirement */
export const submissionRequirements = [
  { id: 'SR1', document_type: 'Technical response', format: 'PDF', max_pages: 20, max_file_size_mb: 25, mandatory: true, special_instructions: 'Arial 11pt minimum. Pages beyond the limit will not be assessed.' },
  { id: 'SR2', document_type: 'Pricing matrix', format: 'XLSX', max_pages: null, max_file_size_mb: 10, mandatory: true, special_instructions: 'Submit on the issued template. Do not alter formulae or add lines.' },
  { id: 'SR3', document_type: 'Social value response', format: 'PDF', max_pages: 8, max_file_size_mb: 10, mandatory: true, special_instructions: 'Must address the PPN 002 model outcome stated at Schedule 7 §1.2.' },
  { id: 'SR4', document_type: 'Certifications bundle', format: 'PDF', max_pages: null, max_file_size_mb: 25, mandatory: true, special_instructions: 'CE+, ISO 27001 and insurance certificates, current at the submission deadline.' },
  { id: 'SR5', document_type: 'Case study annex', format: 'PDF', max_pages: 6, max_file_size_mb: 10, mandatory: false, special_instructions: 'Optional. Not scored; provided for reference verification only.' },
];

/** Conditions of participation — s.22 PA 2023. Failure means the tender must be disregarded. */
export const conditionsOfParticipation = [
  { id: 'CP1', requirement: 'Minimum annual turnover of £2.5m in each of the last two financial years', regulation_reference: 'PA 2023 s.22', category: 'mandatory' as const },
  { id: 'CP2', requirement: 'No mandatory exclusion ground under Schedule 6 applies', regulation_reference: 'PA 2023 Sch. 6', category: 'mandatory' as const },
  { id: 'CP3', requirement: 'Registered on the Central Digital Platform with core supplier information current', regulation_reference: 'PA 2023 s.96', category: 'mandatory' as const },
];

/** models.py :: ComplianceRequirement — the mandatory gate, with regulatory basis. */
export const complianceRequirements = [
  { id: 'M1', category: 'mandatory' as const, requirement: 'Tender received before the stated deadline', regulation_reference: 'ITT §4.1', evidence_required: ['Portal receipt timestamp'] },
  { id: 'M2', category: 'mandatory' as const, requirement: 'Pricing matrix complete on the issued template, all lines priced', regulation_reference: 'ITT §6.2', evidence_required: ['Completed Pricing-Matrix_v3.xlsx'] },
  { id: 'M3', category: 'mandatory' as const, requirement: 'Cyber Essentials Plus certificate current at the submission deadline', regulation_reference: 'Spec §4.1', evidence_required: ['IASME certificate', 'Expiry date'] },
  { id: 'M4', category: 'mandatory' as const, requirement: 'ISO/IEC 27001 certification or demonstrable equivalent assurance', regulation_reference: 'Spec §4.1(b)', evidence_required: ['UKAS-accredited certificate', 'Scope statement'] },
  { id: 'M5', category: 'mandatory' as const, requirement: 'Employer liability insurance of not less than £10m', regulation_reference: 'ITT §7.3', evidence_required: ['Insurance certificate', 'Named insured entity'] },
  { id: 'M6', category: 'mandatory' as const, requirement: 'All mandatory response sections addressed', regulation_reference: 'ITT §5.1', evidence_required: ['Complete technical response'] },
  { id: 'M7', category: 'mandatory' as const, requirement: 'No mandatory exclusion ground declared or identified', regulation_reference: 'PA 2023 Sch. 6', evidence_required: ['Supplier self-declaration', 'CDP record'] },
];

/**
 * Evaluation panel. Under the official guidance each response is scored
 * independently by at least two assessors before moderation to a consensus,
 * and conflict-of-interest declarations form part of the audit trail.
 */
export const panel = [
  { id: 'AS1', name: 'R. Awoyemi', role: 'Procurement Lead', assesses: ['Q1', 'Q3', 'Q4', 'P1'], coi: 'declared_none', coiDate: '2026-08-17' },
  { id: 'AS2', name: 'D. Okonkwo', role: 'Head of IT', assesses: ['Q1', 'Q2', 'Q3'], coi: 'declared_none', coiDate: '2026-08-17' },
  { id: 'AS3', name: 'M. Thorne', role: 'Information Security Officer', assesses: ['Q2', 'Q4'], coi: 'declared_managed', coiDate: '2026-08-17', coiNote: 'Former colleague of a named Havelock engineer (2019–2021). Declared and recorded; not scoring Q3 references.' },
  { id: 'MOD', name: 'S. Bellamy', role: 'Independent Moderator', assesses: [], coi: 'declared_none', coiDate: '2026-08-17', moderator: true },
];

/**
 * Independent assessor bands prior to moderation, keyed bidder → sub-criterion.
 * `rawScores[].band` is the CONSENSUS band agreed at moderation on 24 Aug.
 * A `note` is recorded wherever assessors diverged.
 */
export interface AssessorEntry { a: Band; b: Band; note?: string }

export const assessorMatrix: Record<string, Record<string, AssessorEntry>> = {
  calderwood: {
    'Q1.1': { a: 4, b: 4 },
    'Q1.2': { a: 4, b: 3, note: 'AS2 marked down for single-client evidence. Clarification of 19 Aug supplied the measurement method; consensus agreed at 4.' },
    'Q1.3': { a: 3, b: 3 },
    'Q2.1': { a: 4, b: 4 },
    'Q2.2': { a: 3, b: 3 },
    'Q3.1': { a: 3, b: 3 },
    'Q3.2': { a: 3, b: 2, note: 'AS2 concerned by 0.4 FTE SDM allocation. Panel accepted the wider delivery pool as mitigation; consensus 3.' },
    'Q4.1': { a: 2, b: 2 },
    'Q4.2': { a: 2, b: 2 },
  },
  strathmere: {
    'Q1.1': { a: 3, b: 3 },
    'Q1.2': { a: 3, b: 3 },
    'Q1.3': { a: 4, b: 4 },
    'Q2.1': { a: 3, b: 3 },
    'Q2.2': { a: 4, b: 4 },
    'Q3.1': { a: 3, b: 3 },
    'Q3.2': { a: 3, b: 3 },
    'Q4.1': { a: 4, b: 3, note: 'AS3 initially scored 3 pending verification that the KPI remedies are contractually binding. Draft contract schedule confirmed; consensus 4.' },
    'Q4.2': { a: 4, b: 4 },
  },
  kelverton: {
    'Q1.1': { a: 3, b: 3 },
    'Q1.2': { a: 3, b: 3 },
    'Q1.3': { a: 3, b: 3 },
    'Q2.1': { a: 3, b: 3 },
    'Q2.2': { a: 3, b: 3 },
    'Q3.1': { a: 3, b: 3 },
    'Q3.2': { a: 4, b: 3, note: 'AS1 scored 4 on the 1.0 FTE commitment and named deputies; AS2 scored 3 pending confirmation the surge pool is security-cleared. Clearance evidence supplied 21 Aug; consensus 4.' },
    'Q4.1': { a: 3, b: 3 },
    'Q4.2': { a: 3, b: 3 },
  },
  marchford: {
    'Q1.1': { a: 2, b: 2 },
    'Q1.2': { a: 2, b: 2 },
    'Q1.3': { a: 3, b: 3 },
    'Q2.1': { a: 3, b: 3 },
    'Q2.2': { a: 2, b: 1, note: 'AS3 scored 1 on the absence of 24/7 critical cover required by Spec §4.5. AS2 scored 2, treating the on-call arrangement as partial compliance. Moderated to 2 — the requirement is partially addressed, not absent.' },
    'Q3.1': { a: 2, b: 2 },
    'Q3.2': { a: 2, b: 2 },
    'Q4.1': { a: 3, b: 3 },
    'Q4.2': { a: 2, b: 2 },
  },
  havelock: {
    'Q1.1': { a: 3, b: 3 },
    'Q1.2': { a: 2, b: 2 },
    'Q1.3': { a: 3, b: 3 },
    'Q2.1': { a: 3, b: 1, note: 'Two-band divergence — the only one in the round. AS3 scored 1 on the lapsed ISO 27001; AS2 scored 3 on the accepted recertification evidence. Moderated to 2: the requirement is met, but only by way of equivalence.' },
    'Q2.2': { a: 3, b: 3 },
    'Q3.1': { a: 4, b: 4 },
    'Q3.2': { a: 3, b: 3 },
    'Q4.1': { a: 3, b: 3 },
    'Q4.2': { a: 2, b: 2 },
  },
};

/** models.py :: RiskFactor — contract risk carried into award, distinct from scoring. */
export const riskFactors = [
  {
    risk_id: 'R1', category: 'Information security',
    description: 'Havelock ISO 27001 certificate lapsed 31 May 2026 and recertification has not yet issued. Assurance rests on an audit report rather than a live certificate.',
    probability: 'medium' as RiskLevel, impact: 'high' as RiskLevel,
    mitigation_required: ['Condition award on certificate issue by 31 Dec 2026', 'Quarterly ISMS evidence pack until issued'],
    owner: 'M. Thorne — Information Security Officer',
    detection_confidence: 'high' as ConfidenceLevel,
    source_section: 'Havelock technical response §5.1', source_page: 30,
    source_excerpt: 'Our ISO 27001 recertification audit was completed on 22 July 2026; the certificate is expected to issue in Q4 2026.',
    affects: ['havelock'],
  },
  {
    risk_id: 'R2', category: 'Service continuity',
    description: 'Calderwood evidences business continuity by tabletop exercise only. No live failover has been demonstrated on a comparable estate.',
    probability: 'medium' as RiskLevel, impact: 'high' as RiskLevel,
    mitigation_required: ['Contractual obligation to complete a live failover test within 6 months of service commencement'],
    owner: 'D. Okonkwo — Head of IT',
    detection_confidence: 'high' as ConfidenceLevel,
    source_section: 'Calderwood technical response §6.7.1', source_page: 54,
    source_excerpt: 'The most recent business continuity exercise (February 2026) was conducted as a facilitated tabletop across the service management team.',
    affects: ['calderwood'],
  },
  {
    risk_id: 'R3', category: 'Capacity & resilience',
    description: 'Strathmere offers a delivery pool of nine. Concurrent absence or attrition could constrain the service, particularly in macOS specialism.',
    probability: 'medium' as RiskLevel, impact: 'medium' as RiskLevel,
    mitigation_required: ['Minimum staffing floor written into the contract', 'Named deputy for each key role at mobilisation'],
    owner: 'D. Okonkwo — Head of IT',
    detection_confidence: 'medium' as ConfidenceLevel,
    source_section: 'Strathmere technical response §7.2', source_page: 60,
    source_excerpt: 'The Service Delivery Manager is committed at 0.8 FTE. Six of the nine named engineers hold current Jamf Certified Tech accreditation.',
    affects: ['strathmere'],
  },
  {
    risk_id: 'R5', category: 'Service coverage',
    description: 'Marchford offers support 07:00–19:00 weekdays with out-of-hours on best endeavours. Spec §4.5 requires 24/7 coverage for critical incidents.',
    probability: 'high' as RiskLevel, impact: 'high' as RiskLevel,
    mitigation_required: ['Cannot be mitigated post-award without repricing — the coverage was not offered', 'Reflected in the Q2.2 band rather than carried as a contract condition'],
    owner: 'D. Okonkwo — Head of IT',
    detection_confidence: 'high' as ConfidenceLevel,
    source_section: 'Marchford technical response §5.4', source_page: 29,
    source_excerpt: 'Standard support hours are 07:00 to 19:00 Monday to Friday. Out-of-hours incidents are handled on a best-endeavours basis via the on-call mobile.',
    affects: ['marchford'],
  },
  {
    risk_id: 'R4', category: 'Commercial',
    description: 'Transition assumes 0.5 FTE of Aldercroft technical resource for 14 weeks in the Havelock model — an unbudgeted internal cost not reflected in the tendered price.',
    probability: 'high' as RiskLevel, impact: 'medium' as RiskLevel,
    mitigation_required: ['Quantify internal resource cost before award', 'Reflect in whole-life cost comparison'],
    owner: 'R. Awoyemi — Procurement Lead',
    detection_confidence: 'high' as ConfidenceLevel,
    source_section: 'Havelock technical response §2.2', source_page: 9,
    source_excerpt: 'Transition assumes the availability of a nominated Aldercroft technical contact at approximately 0.5 FTE for the full 14-week period.',
    affects: ['havelock'],
  },
];

export const extraRisk = null;

/** Price-side checks, including the s.19 abnormally low tender test. */
export const priceChecks = [
  {
    id: 'PC1', bidder: 'marchford', label: 'Abnormally low tender test',
    basis: 'Procurement Act 2023 s.19',
    detail: 'Marchford at £3.12m is the lowest compliant tender and sets the price benchmark. It sits 2.3% above the pre-tender estimate — nowhere near the 15% trigger, so no s.19 notification is required.',
    outcome: 'not_triggered' as const,
  },
  {
    id: 'PC2', bidder: 'pellworth', label: 'Abnormally low tender test',
    basis: 'Procurement Act 2023 s.19',
    detail: 'Pellworth at £2.94m is the lowest tender received but was excluded at the compliance gate. The s.19 test is not reached for a tender that has already been disregarded.',
    outcome: 'not_reached' as const,
  },
  {
    id: 'PC3', bidder: 'calderwood', label: 'Affordability ceiling',
    basis: 'ITT §6.2',
    detail: 'Calderwood at £3.48m is 14.1% above the £3.05m pre-tender estimate but within the £3.75m published affordability ceiling.',
    outcome: 'within' as const,
  },
  {
    id: 'PC4', bidder: 'havelock', label: 'Affordability ceiling',
    basis: 'ITT §6.2',
    detail: 'Havelock at £3.69m is 21.0% above the pre-tender estimate and within £60k of the affordability ceiling. Whole-life cost should also account for the 0.5 FTE internal transition resource at R4.',
    outcome: 'within' as const,
  },
];

/**
 * Tender Bank contents available to the issuer flow.
 *
 * Deliberately holds more than the pack for ARA-2026-ITS-014 — documents from
 * the same procurement that are not scoring inputs (clarifications, TUPE, draft
 * contract) and documents from other procurements. Selecting the right five is
 * a judgement, which is what the picker is there to model.
 */
export interface BankDocument {
  id: string;
  name: string;
  kind: string;
  procurement: string;
  procurementRef: string;
  pages: number;
  size: string;
  addedAt: string;
  status: 'indexed' | 'archived';
  /** Required as an input to criteria extraction. */
  required?: boolean;
  /** Why this is (or is not) an extraction input. */
  note?: string;
}

export const tenderBank: BankDocument[] = [
  {
    id: 'd1', name: 'ARA-2026-ITS-014_ITT.pdf', kind: 'ITT',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 18, size: '1.6 MB', addedAt: '2026-06-12', status: 'indexed', required: true,
    note: 'Award criteria and weightings at §5.3.',
  },
  {
    id: 'd2', name: 'Schedule-2_Specification.pdf', kind: 'Specification',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 41, size: '2.7 MB', addedAt: '2026-06-12', status: 'indexed', required: true,
    note: 'Sub-criteria and mandatory requirements.',
  },
  {
    id: 'd3', name: 'Schedule-4_Evaluation-Model.xlsx', kind: 'Evaluation model',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 6, size: '188 KB', addedAt: '2026-06-12', status: 'indexed', required: true,
    note: 'Weighting matrix, used to cross-check the ITT.',
  },
  {
    id: 'd4', name: 'Schedule-7_Social-Value.pdf', kind: 'Social value',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 12, size: '640 KB', addedAt: '2026-06-12', status: 'indexed', required: true,
    note: 'PPN 002 model outcome and social value sub-criteria.',
  },
  {
    id: 'd5', name: 'Pricing-Matrix_v3.xlsx', kind: 'Pricing',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 4, size: '96 KB', addedAt: '2026-06-18', status: 'indexed', required: true,
    note: 'Price model and affordability ceiling.',
  },
  {
    id: 'd6', name: 'ARA-2026-ITS-014_Clarifications-Log.pdf', kind: 'Clarifications',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 9, size: '410 KB', addedAt: '2026-07-17', status: 'indexed',
    note: 'Issued after the model was published — not a scoring input.',
  },
  {
    id: 'd7', name: 'Schedule-3_TUPE-Information.pdf', kind: 'TUPE',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 18, size: '1.1 MB', addedAt: '2026-06-12', status: 'indexed',
    note: 'Informational. Carries no award criteria.',
  },
  {
    id: 'd8', name: 'ARA-2026-ITS-014_Draft-Contract.pdf', kind: 'Contract',
    procurement: 'Provision of Managed IT Services 2027–2030', procurementRef: 'ARA-2026-ITS-014',
    pages: 54, size: '3.3 MB', addedAt: '2026-06-12', status: 'indexed',
    note: 'Terms, not evaluation criteria.',
  },
  {
    id: 'd9', name: 'MRC-2026-FM-007_ITT.pdf', kind: 'ITT',
    procurement: 'Facilities Management 2027–2032', procurementRef: 'MRC-2026-FM-007',
    pages: 74, size: '5.1 MB', addedAt: '2026-07-30', status: 'indexed',
    note: 'Different procurement.',
  },
  {
    id: 'd10', name: 'HEF-2025-NET-002_Specification.pdf', kind: 'Specification',
    procurement: 'Network Refresh 2025–2028', procurementRef: 'HEF-2025-NET-002',
    pages: 33, size: '2.2 MB', addedAt: '2025-11-04', status: 'archived',
    note: 'Closed procurement, retained for reference.',
  },
];

export const requiredDocIds = tenderBank.filter(d => d.required).map(d => d.id);
